import React, { useState } from 'react';
import { CourseTrajectoryItem, DoctoralProcessOverview } from '../types';

interface DoctoralOverviewViewProps {
  data: DoctoralProcessOverview | null;
  onNavigate: (viewId: string) => void;
  onOpenModal: (modalId: string, payload?: unknown) => void;
}

const YEAR_COLORS = ['#42d8ff', '#4ce2bf', '#9b7cff'];

const FALLBACK_COURSES: CourseTrajectoryItem[] = [
  {
    year: 1,
    title: '1 курс',
    subtitle: 'Теория · тема · ИУП · НИРД-1/2',
    credits: 60,
    semester_1_title: '1 семестр',
    semester_1_items: ['Утверждение темы', 'Индивидуальный план', 'НИРД-1'],
    semester_2_title: '2 семестр',
    semester_2_items: ['НИРД-2', 'Публикационная подготовка'],
  },
  {
    year: 2,
    title: '2 курс',
    subtitle: 'НИРД-3/4 · педагогическая и исследовательская практики',
    credits: 60,
    semester_1_title: '3 семестр',
    semester_1_items: ['НИРД-3', 'Педагогическая практика'],
    semester_2_title: '4 семестр',
    semester_2_items: ['НИРД-4', 'Исследовательская практика'],
  },
  {
    year: 3,
    title: '3 курс',
    subtitle: 'НИРД-5/6 · стажировка · финальная готовность',
    credits: 60,
    semester_1_title: '5 семестр',
    semester_1_items: ['НИРД-5', 'Научная стажировка'],
    semester_2_title: '6 семестр',
    semester_2_items: ['НИРД-6', 'Предзащита'],
  },
];

const SECTION_ROUTE: Record<string, string> = {
  profile: 'profile',
  topic: 'topic',
  plan: 'plan',
  nird: 'nird',
  practice: 'practices',
  practices: 'practices',
  internship: 'internship',
  publications: 'publications',
  completion: 'completion',
};

export const DoctoralOverviewView: React.FC<DoctoralOverviewViewProps> = ({
  data,
  onNavigate,
}) => {
  const readiness = data?.overall_readiness ?? 0;
  const year = data?.current_year ?? null;
  const semester = data?.current_semester ?? null;
  const courses = data?.courses?.length ? data.courses : FALLBACK_COURSES;
  const nextActions = data?.next_actions ?? [];
  const sections = data?.sections_readiness ?? [];
  const [selected, setSelected] = useState(year ?? courses[0]?.year ?? 1);
  const current = courses.find((c) => c.year === selected) ?? courses[0];
  const programCode = data?.program_code || '';
  const programTitle = data?.program_title || '';
  const programLine = [programCode, programTitle].filter(Boolean).join(' · ') || 'Программа не назначена';

  return (
    <>
      <section className="dp120-hero" style={{ ['--a' as string]: '#42d8ff' }}>
        <div className="kicker">Обучение в докторантуре{programCode ? ` · ${programCode}` : ''}</div>
        <h2>Моя докторская траектория</h2>
        <p>
          Трёхлетний путь показан целиком: текущий курс выделен, но любой будущий курс можно
          открыть заранее. Это позволяет с первого дня видеть, что потребуется до завершения
          программы, предзащиты и защиты.
        </p>
        <div className="dp120-hero-actions">
          <button type="button" className="dp120-btn gold" onClick={() => onNavigate('profile')}>
            Продолжить заполнение →
          </button>
          <button type="button" className="dp120-btn" onClick={() => onNavigate('guide')}>
            Как устроена докторантура
          </button>
        </div>
      </section>

      <section className="dp120-trajectory">
        <div className="dp120-progress-ring" style={{ ['--v' as string]: String(readiness) }}>
          <div className="inside">
            <b>{`${readiness}%`}</b>
            <span>общая готовность</span>
          </div>
        </div>
        <div className="dp120-trajectory-copy">
          <div style={{ ['--c' as string]: '#42d8ff' }}>
            <small>СЕЙЧАС</small>
            <strong>
              {year ? `${year} курс${semester ? ` · ${semester} семестр` : ''}` : 'Курс ещё не указан'}
            </strong>
            <p>Текущий этап выделен, будущие этапы доступны для просмотра.</p>
          </div>
          <div style={{ ['--c' as string]: '#4ce2bf' }}>
            <small>ПРОГРАММА 2025</small>
            <strong>3 года · {data?.program_credits_total ?? 180} кредитов</strong>
            <p>
              {programCode || programTitle ? (
                <>
                  {programCode}
                  {programCode && programTitle ? ' · ' : ''}
                  {programTitle ? <span>{programTitle}</span> : null}
                </>
              ) : (
                programLine
              )}
            </p>
          </div>
          <div style={{ ['--c' as string]: '#9b7cff' }}>
            <small>ПОСЛЕ ПРОГРАММЫ</small>
            <strong>Защита может быть позже</strong>
            <p>Кабинет остаётся активным для защиты, диплома и выпускных документов.</p>
          </div>
        </div>
      </section>

      <div className="dp120-year-grid">
        {courses.map((course, index) => {
          const color = YEAR_COLORS[index % YEAR_COLORS.length];
          const isCurrent = course.year === year;
          return (
            <button
              key={course.year}
              type="button"
              className={`dp120-year${isCurrent ? ' current' : ''}${selected === course.year ? ' selected' : ''}`}
              style={{ ['--y' as string]: color }}
              onClick={() => setSelected(course.year)}
            >
              <span className="year-no">{String(index + 1).padStart(2, '0')}</span>
              <h3>{course.title}</h3>
              <p>{course.subtitle}</p>
              <div className="dp120-semesters">
                <div className="dp120-sem">
                  <b>{course.credits} кредитов</b>
                  <span>Открыть подробное содержание и будущие действия</span>
                </div>
              </div>
              <span className="open-course">
                <span>Открыть содержание курса</span>
                <em>→</em>
              </span>
            </button>
          );
        })}
      </div>

      {current && (
        <section
          className="dp120-course-detail"
          style={{ ['--c' as string]: YEAR_COLORS[(current.year - 1) % YEAR_COLORS.length] }}
        >
          <div className="dp120-course-detail-head">
            <div>
              <span className={`dp120-badge${current.year === year ? ' good' : ''}`}>
                {current.year === year ? 'ТЕКУЩИЙ КУРС' : 'МОЖНО ПОСМОТРЕТЬ ЗАРАНЕЕ'}
              </span>
              <h3>
                {current.title} · {current.credits} кредитов
              </h3>
              <p>{current.subtitle}</p>
            </div>
            <button type="button" className="dp120-mini" onClick={() => onNavigate('guide')}>
              Читать в путеводителе
            </button>
          </div>
          <div className="dp120-course-sem-grid">
            <div className="dp120-course-sem">
              <b>{current.semester_1_title}</b>
              <ul>
                {current.semester_1_items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className="dp120-course-sem">
              <b>{current.semester_2_title}</b>
              <ul>
                {current.semester_2_items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <div className="dp120-course-actions">
            <button type="button" className="dp120-mini" onClick={() => onNavigate('plan')}>
              Индивидуальный план
            </button>
            <button type="button" className="dp120-mini" onClick={() => onNavigate('nird')}>
              НИРД
            </button>
            <button type="button" className="dp120-mini" onClick={() => onNavigate('practices')}>
              Практики
            </button>
          </div>
        </section>
      )}

      <div style={{ height: 11 }} />

      <div className="dp120-grid">
        <article className="dp120-card span8">
          <header>
            <div>
              <h3>Мой маршрут</h3>
              <p>Система показывает ближайшие осмысленные действия, а не длинный список анкеты.</p>
            </div>
          </header>
          <div className="dp120-card-body">
            <div className="dp120-next">
              {nextActions.length === 0 && (
                <div className="dp120-empty">Ближайшие шаги появятся после загрузки досье.</div>
              )}
              {nextActions.map((action) => (
                <button
                  key={action.num + action.title}
                  type="button"
                  onClick={() => onNavigate(action.route_key)}
                >
                  <b>{action.num}</b>
                  <span>{action.title}</span>
                  <em>→</em>
                </button>
              ))}
            </div>
          </div>
        </article>

        <article className="dp120-card span4">
          <header>
            <div>
              <h3>Готовность разделов</h3>
              <p>Заполняется постепенно в течение трёх лет.</p>
            </div>
          </header>
          <div className="dp120-card-body">
            <div className="dp120-list">
              {sections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  className="dp120-row"
                  onClick={() => onNavigate(SECTION_ROUTE[section.key] || section.key)}
                  style={{ width: '100%', color: 'inherit', cursor: 'pointer', textAlign: 'left' }}
                >
                  <span>
                    <strong>{section.title}</strong>
                    <small>{section.percentage}% заполнено</small>
                    <div className="dp120-progress" style={{ ['--p' as string]: `${section.percentage}%` }}>
                      <i />
                    </div>
                  </span>
                </button>
              ))}
              {sections.length === 0 && (
                <div className="dp120-empty">Разделы досье ещё не рассчитаны.</div>
              )}
            </div>
          </div>
        </article>
      </div>
    </>
  );
};
